// Grid picker for avatars — suit glyphs, emoji, and DiceBear.
//
// Usage: controlled modal opened from Profile page.
//   <AvatarPickerModal open={open} currentAvatar={profile.avatar}
//                      onSelect={(id) => saveAvatar(id)} onClose={close} />
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import AvatarRender from "./AvatarRender";
import { AVATAR_CATALOG } from "../lib/avatars_catalog";
import { sfx } from "../lib/sound";

const TABS = [
  { key: "suit",  label: "Suits" },
  { key: "emoji", label: "Emoji" },
  { key: "dice",  label: "Faces" },
];

export default function AvatarPickerModal({ open, currentAvatar, onSelect, onClose }) {
  const [tab, setTab] = useState("suit");
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);

  const choice = picked || currentAvatar || "default";

  const doSave = async () => {
    if (!picked || picked === currentAvatar) { onClose?.(); return; }
    setSaving(true);
    try { await onSelect?.(picked); } finally {
      setSaving(false);
      setPicked(null);
      onClose?.();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)" }}
          onClick={onClose}
          data-testid="avatar-picker-modal"
        >
          <motion.div
            initial={{ scale: 0.94, y: 12 }} animate={{ scale: 1, y: 0 }}
            className="glass max-w-md w-full p-6 rounded-3xl relative"
            style={{ maxHeight: "92vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-eyebrow" style={{ color: "#FFD700" }}>CHOOSE AVATAR</div>
                <h2 className="font-display font-black text-xl">Your face in the game</h2>
              </div>
              <button
                className="btn-ghost text-xs"
                onClick={onClose}
                data-testid="avatar-picker-close-btn"
              >Close</button>
            </div>

            {/* Preview */}
            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl"
                 style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.1)" }}>
              <AvatarRender avatar={choice} name="You" size={56} />
              <div className="text-xs text-white/65 flex-1">
                {picked && picked !== currentAvatar
                  ? <span><b style={{ color: "#3DDC97" }}>Preview</b> — tap save to apply.</span>
                  : <span>This is how others will see you in the lobby &amp; during play.</span>}
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-3" data-testid="avatar-picker-tabs">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold tracking-wide uppercase ${tab === t.key ? "" : "opacity-60"}`}
                  onClick={() => { sfx.click(); setTab(t.key); }}
                  data-testid={`avatar-picker-tab-${t.key}`}
                  style={{
                    background: tab === t.key ? "#FFD700" : "rgba(255,255,255,0.05)",
                    color: tab === t.key ? "#0B0B0B" : "#fff",
                    border: tab === t.key ? "1px solid #FFD700" : "1px solid rgba(255,255,255,0.08)",
                  }}
                >{t.label}</button>
              ))}
            </div>

            {/* Grid */}
            <div
              className="grid grid-cols-4 sm:grid-cols-6 gap-3 overflow-y-auto"
              style={{ maxHeight: "45vh" }}
              data-testid={`avatar-picker-grid-${tab}`}
            >
              {AVATAR_CATALOG[tab].map((it) => {
                const id = it.id;
                const active = choice === id;
                return (
                  <button
                    key={id}
                    onClick={() => { sfx.click(); setPicked(id); }}
                    className="relative flex items-center justify-center p-1 rounded-xl transition-all"
                    style={{
                      background: active ? "rgba(255,215,0,0.14)" : "rgba(255,255,255,0.03)",
                      border: active ? "2px solid #FFD700" : "1px solid rgba(255,255,255,0.08)",
                      transform: active ? "scale(1.04)" : "scale(1)",
                    }}
                    data-testid={`avatar-option-${id.replace(/:/g, '-')}`}
                    aria-label={`Choose ${it.label || it.seed || it.emoji || id}`}
                  >
                    <AvatarRender avatar={id} size={52} />
                  </button>
                );
              })}
            </div>

            <button
              className="btn-primary w-full mt-5"
              onClick={doSave}
              disabled={saving || !picked || picked === currentAvatar}
              data-testid="avatar-picker-save-btn"
              style={{ background: "#FFD700", color: "#0B0B0B", opacity: (!picked || picked === currentAvatar) ? 0.5 : 1 }}
            >
              {saving ? "Saving…" : (picked && picked !== currentAvatar) ? "Save avatar" : "Pick one above"}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
